import type { WaypointMarkerModel } from './waypointMarker'
import { MarkerEventType } from '../../../types/elements/visualizer/visualizerTypes'

/**
 * Sets up all MarkerEventType listeners on the given WaypointMarkerModel mesh.
 * This function stores the listeners on the model to allow for later removal.
 * @param markerModel The WaypointMarkerModel instance.
 */
function setWaypointModelEventListeners(markerModel: WaypointMarkerModel): void {
  // Initialize the listener map if it doesn't already exist.
  if (!markerModel._listeners) {
    markerModel._listeners = new Map()
  }

  Object.values(MarkerEventType).forEach((eventType: MarkerEventType) => {
    // Create a concrete listener function that can be stored and referenced later.
    const listener = () => {
      if (typeof markerModel.eventCallback === 'function') {
        markerModel.eventCallback(eventType, markerModel)
      }
    }

    // Store the listener on the model's map for future removal.
    markerModel._listeners.set(eventType, listener)
    // Add the event listener to the mesh.
    markerModel.mesh.addEventListener(eventType, listener)
  })
}

/**
 * Removes all event listeners set up by setWaypointModelEventListeners.
 * This should be called from a `destroy()` or `cleanup()` method on the model
 * to prevent memory leaks.
 * @param markerModel The WaypointMarkerModel instance.
 */
function removeWaypointModelEventListeners(markerModel: WaypointMarkerModel): void {
  // If there's no listener map, there's nothing to remove.
  if (!markerModel._listeners) {
    return
  }

  // Iterate over the stored listeners and remove each one from the mesh.
  markerModel._listeners.forEach((listener, eventType) => {
    markerModel.mesh.removeEventListener(eventType, listener)
  })

  // Clear the map to release all references and prevent memory leaks.
  markerModel._listeners.clear()
}

export { setWaypointModelEventListeners, removeWaypointModelEventListeners }
